import React from 'react';
import { Box, Flex, Text, HStack } from '@chakra-ui/react';

const healthColor = (rate) => {
  if (rate >= 90) return 'status.pass';
  if (rate >= 70) return 'status.flaky';
  return 'status.fail';
};

const fmtWhen = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

/**
 * Flat group tile per the design language. The whole card opens the group;
 * a secondary "History" affordance opens the run history.
 */
const TestGroupCard = ({ name, metrics = {}, onView, onHistory }) => {
  const total = Number(metrics.total) || 0;
  const passed = Number(metrics.passed) || 0;
  const failed = Number(metrics.failed) || 0;
  const skipped = Number(metrics.skipped) || 0;
  const passRate = Number(metrics.passRate) || 0;
  const denom = total || passed + failed + skipped || 1;
  const pct = (n) => `${((100 * n) / denom).toFixed(1)}%`;

  return (
    <Box
      onClick={onView}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onView?.();
        }
      }}
      role="button"
      tabIndex={0}
      cursor="pointer"
      bg="white"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="16px"
      boxShadow="0 1px 2px rgba(0, 0, 0, 0.04)"
      p={5}
      transition="border-color 0.15s, box-shadow 0.15s"
      _hover={{ borderColor: 'gray.300', boxShadow: '0 4px 14px rgba(0,0,0,0.06)' }}
      _focusVisible={{ outline: '2px solid', outlineColor: 'brand.500', outlineOffset: '2px' }}
    >
      <Flex align="flex-start" justify="space-between" gap={3} mb={3}>
        <HStack spacing={2} minW={0}>
          <Box w="8px" h="8px" borderRadius="full" bg={healthColor(passRate)} flexShrink={0} />
          <Text fontWeight="700" color="gray.900" noOfLines={1}>{name}</Text>
        </HStack>
        <Text fontSize="lg" fontWeight="800" letterSpacing="-0.5px" color="gray.900" flexShrink={0}>
          {passRate.toFixed(1)}%
        </Text>
      </Flex>

      <Flex h="7px" borderRadius="full" overflow="hidden" bg="gray.100" mb={2}>
        <Box w={pct(passed)} bg="status.pass" />
        <Box w={pct(failed)} bg="status.fail" />
        <Box w={pct(skipped)} bg="status.skip" />
      </Flex>

      <HStack spacing={3} fontSize="12px" color="gray.500" mb={3}>
        <Text><Box as="span" color="status.pass" fontWeight="700">{passed}</Box> passed</Text>
        <Text><Box as="span" color="status.fail" fontWeight="700">{failed}</Box> failed</Text>
        <Text><Box as="span" color="gray.600" fontWeight="700">{skipped}</Box> skipped</Text>
      </HStack>

      <Flex align="center" justify="space-between">
        <Text fontSize="11px" color="gray.400">Last run {fmtWhen(metrics.lastRun)}</Text>
        {onHistory && (
          <Text
            as="span"
            fontSize="12px"
            fontWeight="600"
            color="brand.600"
            _hover={{ color: 'brand.500' }}
            onClick={(e) => {
              e.stopPropagation();
              onHistory();
            }}
          >
            History →
          </Text>
        )}
      </Flex>
    </Box>
  );
};

export default TestGroupCard;
